/**
 * React Query mutation hook for updating ShowNotes blocks
 */

import { useMutation } from '@tanstack/react-query'
import httpClient from 'src/utilities/http-client'
import {
  ShowNotesBlock,
  ShowNotesTextBlock,
  ShowNotesImageBlock,
  ShowNotesPDFBlock,
} from '../types'

interface UpdateTextBlockRequest {
  markdown: string
  sequence: number
}

interface UpdateImageBlockRequest {
  imageUrl: string
  caption: string
  altText: string
  widthPercent: number
  sequence: number
}

interface UpdatePDFBlockRequest {
  pdfUrl: string
  fileName: string
  fileSize: number
  caption: string | null
  sequence: number
}

/**
 * Update a text block via API
 */
async function updateTextBlock(
  blockID: string,
  data: UpdateTextBlockRequest,
  basePath: string,
): Promise<void> {
  await httpClient.put(`/api/${basePath}/blocks/${blockID}`, data)
}

/**
 * Update an image block via API
 */
async function updateImageBlock(
  blockID: string,
  data: UpdateImageBlockRequest,
  basePath: string,
): Promise<void> {
  await httpClient.put(`/api/${basePath}/blocks/${blockID}`, data)
}

/**
 * Update a PDF block via API
 */
async function updatePDFBlock(
  blockID: string,
  data: UpdatePDFBlockRequest,
  basePath: string,
): Promise<void> {
  await httpClient.put(`/api/${basePath}/blocks/${blockID}`, data)
}

/**
 * Hook to update a show notes block
 * No cache invalidation needed - individual block updates don't affect structure
 */
export function useUpdateBlock(basePath: string) {
  return useMutation({
    mutationFn: (block: ShowNotesBlock) => {
      if (block.blockType === 'text') {
        let textBlock = block as ShowNotesTextBlock
        return updateTextBlock(
          block.id,
          {
            markdown: textBlock.markdown,
            sequence: textBlock.sequence,
          },
          basePath,
        )
      }
      if (block.blockType === 'image') {
        let imageBlock = block as ShowNotesImageBlock
        return updateImageBlock(
          block.id,
          {
            imageUrl: imageBlock.imageUrl,
            caption: imageBlock.caption,
            altText: imageBlock.altText,
            widthPercent: imageBlock.widthPercent,
            sequence: imageBlock.sequence,
          },
          basePath,
        )
      }
      let pdfBlock = block as ShowNotesPDFBlock
      return updatePDFBlock(
        block.id,
        {
          pdfUrl: pdfBlock.pdfUrl,
          fileName: pdfBlock.fileName,
          fileSize: pdfBlock.fileSize,
          caption: pdfBlock.caption,
          sequence: pdfBlock.sequence,
        },
        basePath,
      )
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to update block:', error)
    },
  })
}
